/*
 * Codice QR: codificatore minimo per il gestionale CIDIS (secondo fattore, 31/10/2026).
 *
 * Provenienza: riscritto in TypeScript sullo schema della libreria «QR Code generator» di Project
 * Nayuki (https://www.nayuki.io/page/qr-code-generator-library), licenza MIT, ridotto a cio' che
 * serve qui: solo la modalita' byte (UTF-8), versioni da 1 a 40, scelta automatica della maschera.
 * Sta qui come file unico e non come pacchetto npm: un pacchetto nuovo andrebbe aggiunto alle
 * immagini Docker, e per cento righe di algoritmo noto non vale la pena.
 *
 * Copyright (c) Project Nayuki. (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 * - The above copyright notice and this permission notice shall be included in
 *   all copies or substantial portions of the Software.
 * - The Software is provided "as is", without warranty of any kind, express or
 *   implied, including but not limited to the warranties of merchantability,
 *   fitness for a particular purpose and noninfringement. In no event shall the
 *   authors or copyright holders be liable for any claim, damages or other
 *   liability, whether in an action of contract, tort or otherwise, arising from,
 *   out of or in connection with the Software or the use or other dealings in the
 *   Software.
 */

/** Livello di correzione degli errori: M recupera circa il 15% dei moduli, ed e' il consueto. */
export type Correzione = 'L' | 'M' | 'Q' | 'H'

const INDICE: Record<Correzione, number> = { L: 0, M: 1, Q: 2, H: 3 }
/** Bit del formato per ciascun livello (ISO/IEC 18004, tabella 12). */
const BIT_FORMATO: Record<Correzione, number> = { L: 1, M: 0, Q: 3, H: 2 }

// Codici di correzione per blocco e numero di blocchi, per livello (L, M, Q, H) e versione (1-40).
const CORREZIONE_PER_BLOCCO: number[][] = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
]
const NUMERO_BLOCCHI: number[][] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
]

const bit = (x: number, i: number) => ((x >>> i) & 1) !== 0

/** Moduli utili ai dati in una versione, tolti tutti quelli delle figure fisse. */
function moduliDati(ver: number): number {
  let n = (16 * ver + 128) * ver + 64
  if (ver >= 2) {
    const allineamenti = Math.floor(ver / 7) + 2
    n -= (25 * allineamenti - 10) * allineamenti - 55
    if (ver >= 7) n -= 36
  }
  return n
}

function parolePerDati(ver: number, c: Correzione): number {
  const l = INDICE[c]
  return Math.floor(moduliDati(ver) / 8) - CORREZIONE_PER_BLOCCO[l][ver] * NUMERO_BLOCCHI[l][ver]
}

// --- Reed-Solomon su GF(2^8), polinomio 0x11D ------------------------------------------------------

function moltiplica(x: number, y: number): number {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z
}

function divisore(grado: number): number[] {
  const r: number[] = Array.from({ length: grado }, (_, i) => (i === grado - 1 ? 1 : 0))
  let radice = 1
  for (let i = 0; i < grado; i++) {
    for (let j = 0; j < r.length; j++) {
      r[j] = moltiplica(r[j], radice)
      if (j + 1 < r.length) r[j] ^= r[j + 1]
    }
    radice = moltiplica(radice, 0x02)
  }
  return r
}

function resto(dati: number[], div: number[]): number[] {
  const r = div.map(() => 0)
  for (const b of dati) {
    const fattore = b ^ (r.shift() as number)
    r.push(0)
    div.forEach((coef, i) => { r[i] ^= moltiplica(coef, fattore) })
  }
  return r
}

// --- la matrice ------------------------------------------------------------------------------------

class Matrice {
  readonly ver: number
  readonly c: Correzione
  readonly lato: number
  readonly moduli: boolean[][]
  readonly fisso: boolean[][]

  constructor(ver: number, c: Correzione) {
    this.ver = ver
    this.c = c
    this.lato = ver * 4 + 17
    this.moduli = Array.from({ length: this.lato }, () => Array<boolean>(this.lato).fill(false))
    this.fisso = Array.from({ length: this.lato }, () => Array<boolean>(this.lato).fill(false))
  }

  imposta(x: number, y: number, scuro: boolean) {
    this.moduli[y][x] = scuro
    this.fisso[y][x] = true
  }

  figureFisse() {
    const n = this.lato
    for (let i = 0; i < n; i++) {
      this.imposta(6, i, i % 2 === 0)
      this.imposta(i, 6, i % 2 === 0)
    }
    this.cercatore(3, 3)
    this.cercatore(n - 4, 3)
    this.cercatore(3, n - 4)
    const pos = this.posizioniAllineamento()
    const k = pos.length
    for (let i = 0; i < k; i++) {
      for (let j = 0; j < k; j++) {
        // Non sopra i tre cercatori.
        if ((i === 0 && j === 0) || (i === 0 && j === k - 1) || (i === k - 1 && j === 0)) continue
        this.allineamento(pos[i], pos[j])
      }
    }
    this.formato(0)
    this.versione()
  }

  cercatore(x: number, y: number) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy))
        const xx = x + dx, yy = y + dy
        if (xx >= 0 && xx < this.lato && yy >= 0 && yy < this.lato) this.imposta(xx, yy, d !== 2 && d !== 4)
      }
    }
  }

  allineamento(x: number, y: number) {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) this.imposta(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
    }
  }

  posizioniAllineamento(): number[] {
    if (this.ver === 1) return []
    const k = Math.floor(this.ver / 7) + 2
    const passo = Math.floor((this.ver * 8 + k * 3 + 5) / (k * 4 - 4)) * 2
    const r = [6]
    for (let p = this.lato - 7; r.length < k; p -= passo) r.splice(1, 0, p)
    return r
  }

  /** Livello e maschera, con il loro codice BCH, nelle due copie; piu' il modulo sempre scuro. */
  formato(maschera: number) {
    const dati = (BIT_FORMATO[this.c] << 3) | maschera
    let r = dati
    for (let i = 0; i < 10; i++) r = (r << 1) ^ ((r >>> 9) * 0x537)
    const b = ((dati << 10) | r) ^ 0x5412
    const n = this.lato
    for (let i = 0; i <= 5; i++) this.imposta(8, i, bit(b, i))
    this.imposta(8, 7, bit(b, 6))
    this.imposta(8, 8, bit(b, 7))
    this.imposta(7, 8, bit(b, 8))
    for (let i = 9; i < 15; i++) this.imposta(14 - i, 8, bit(b, i))
    for (let i = 0; i < 8; i++) this.imposta(n - 1 - i, 8, bit(b, i))
    for (let i = 8; i < 15; i++) this.imposta(8, n - 15 + i, bit(b, i))
    this.imposta(8, n - 8, true)
  }

  /** Dalla versione 7: il numero di versione con il suo codice BCH, nei due angoli. */
  versione() {
    if (this.ver < 7) return
    let r = this.ver
    for (let i = 0; i < 12; i++) r = (r << 1) ^ ((r >>> 11) * 0x1f25)
    const b = (this.ver << 12) | r
    for (let i = 0; i < 18; i++) {
      const a = this.lato - 11 + (i % 3), c = Math.floor(i / 3)
      this.imposta(a, c, bit(b, i))
      this.imposta(c, a, bit(b, i))
    }
  }

  /** I codici, a zig-zag da destra in basso, saltando le figure fisse e la colonna di temporizzazione. */
  parole(dati: number[]) {
    let i = 0
    const n = this.lato
    for (let destra = n - 1; destra >= 1; destra -= 2) {
      if (destra === 6) destra = 5
      for (let v = 0; v < n; v++) {
        for (let j = 0; j < 2; j++) {
          const x = destra - j
          const su = ((destra + 1) & 2) === 0
          const y = su ? n - 1 - v : v
          if (!this.fisso[y][x] && i < dati.length * 8) {
            this.moduli[y][x] = bit(dati[i >>> 3], 7 - (i & 7))
            i++
          }
        }
      }
    }
  }

  maschera(m: number) {
    for (let y = 0; y < this.lato; y++) {
      for (let x = 0; x < this.lato; x++) {
        let inverti: boolean
        switch (m) {
          case 0: inverti = (x + y) % 2 === 0; break
          case 1: inverti = y % 2 === 0; break
          case 2: inverti = x % 3 === 0; break
          case 3: inverti = (x + y) % 3 === 0; break
          case 4: inverti = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break
          case 5: inverti = ((x * y) % 2) + ((x * y) % 3) === 0; break
          case 6: inverti = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break
          default: inverti = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
        }
        if (!this.fisso[y][x] && inverti) this.moduli[y][x] = !this.moduli[y][x]
      }
    }
  }

  /** Penalita' della norma (regole N1-N4): la maschera con la piu' bassa si legge meglio. */
  penalita(): number {
    const n = this.lato, m = this.moduli
    let p = 0
    // N1: file di cinque o piu' moduli uguali, per righe e per colonne.
    for (let a = 0; a < n; a++) {
      for (const leggi of [(i: number) => m[a][i], (i: number) => m[i][a]]) {
        let fila = 1
        for (let i = 1; i <= n; i++) {
          if (i < n && leggi(i) === leggi(i - 1)) { fila++; continue }
          if (fila >= 5) p += 3 + (fila - 5)
          fila = 1
        }
      }
    }
    // N2: quadrati 2x2 dello stesso colore.
    for (let y = 0; y < n - 1; y++) {
      for (let x = 0; x < n - 1; x++) {
        const c = m[y][x]
        if (c === m[y][x + 1] && c === m[y + 1][x] && c === m[y + 1][x + 1]) p += 3
      }
    }
    // N3: lo schema del cercatore (1:1:3:1:1) con quattro moduli chiari da un lato.
    const schemi = ['10111010000', '00001011101']
    for (let a = 0; a < n; a++) {
      const riga = m[a].map((v) => (v ? '1' : '0')).join('')
      const colonna = m.map((r) => (r[a] ? '1' : '0')).join('')
      for (const s of [riga, colonna]) {
        for (let i = 0; i + 11 <= n; i++) if (schemi.includes(s.slice(i, i + 11))) p += 40
      }
    }
    // N4: equilibrio fra scuri e chiari.
    let scuri = 0
    for (const r of m) for (const v of r) if (v) scuri++
    const totale = n * n
    p += Math.floor(Math.abs(scuri * 20 - totale * 10) / totale) * 10
    return p
  }
}

/** I codici di dati con la correzione, divisi in blocchi e intercalati. */
function conCorrezione(dati: number[], ver: number, c: Correzione): number[] {
  const l = INDICE[c]
  const blocchi = NUMERO_BLOCCHI[l][ver]
  const corr = CORREZIONE_PER_BLOCCO[l][ver]
  const totali = Math.floor(moduliDati(ver) / 8)
  const corti = blocchi - (totali % blocchi)
  const lunghezzaCorti = Math.floor(totali / blocchi)
  const div = divisore(corr)
  const parti: number[][] = []
  for (let i = 0, k = 0; i < blocchi; i++) {
    const d = dati.slice(k, k + lunghezzaCorti - corr + (i < corti ? 0 : 1))
    k += d.length
    const e = resto(d, div)
    if (i < corti) d.push(0)
    parti.push(d.concat(e))
  }
  const r: number[] = []
  for (let i = 0; i < parti[0].length; i++) {
    parti.forEach((b, j) => {
      // Il segnaposto aggiunto ai blocchi corti non si trasmette.
      if (i !== lunghezzaCorti - corr || j >= corti) r.push(b[i])
    })
  }
  return r
}

/**
 * Il codice QR di un testo: una matrice di moduli, `true` scuro. Senza margine: il margine chiaro di
 * quattro moduli lo aggiunge chi disegna.
 */
export function codiceQr(testo: string, c: Correzione = 'M'): boolean[][] {
  const byte = Array.from(new TextEncoder().encode(testo))
  let ver = 1
  for (; ver <= 40; ver++) {
    const lunghezzaConteggio = ver <= 9 ? 8 : 16
    if (4 + lunghezzaConteggio + byte.length * 8 <= parolePerDati(ver, c) * 8) break
  }
  if (ver > 40) throw new Error('Testo troppo lungo per un codice QR')

  // Modalita' byte (0100), lunghezza, dati; poi terminatore, allineamento al byte e riempimento.
  const bits: number[] = []
  const aggiungi = (valore: number, n: number) => { for (let i = n - 1; i >= 0; i--) bits.push((valore >>> i) & 1) }
  aggiungi(0b0100, 4)
  aggiungi(byte.length, ver <= 9 ? 8 : 16)
  for (const b of byte) aggiungi(b, 8)
  const capienza = parolePerDati(ver, c) * 8
  aggiungi(0, Math.min(4, capienza - bits.length))
  aggiungi(0, (8 - (bits.length % 8)) % 8)
  for (let riempi = 0xec; bits.length < capienza; riempi ^= 0xec ^ 0x11) aggiungi(riempi, 8)
  const dati: number[] = []
  for (let i = 0; i < bits.length; i += 8) dati.push(parseInt(bits.slice(i, i + 8).join(''), 2))

  const q = new Matrice(ver, c)
  q.figureFisse()
  q.parole(conCorrezione(dati, ver, c))
  let migliore = 0, minima = Infinity
  for (let m = 0; m < 8; m++) {
    q.maschera(m)
    q.formato(m)
    const p = q.penalita()
    if (p < minima) { minima = p; migliore = m }
    q.maschera(m) // la maschera e' uno XOR: riapplicata, si toglie
  }
  q.maschera(migliore)
  q.formato(migliore)
  return q.moduli
}
